<template>
  <v-container>
    <back-button :label="transPlural('settlement.settlement')"></back-button>

    <page-heading>{{ pageTitle }}</page-heading>

    <v-btn v-if="isDevelopment" @click="validateBookings">Re-Validate</v-btn>

    <request-state
      :request-promise="settlementGeneratePromise || validationPromise"
      :loading-text="isSettlementGenerating ? $t('settlement.isGenerating', {monthName: getMonthNameByIndex(settlement.month), year: settlement.year}) : null"
    >
      <template v-if="bookingValidationResult">
        <v-alert text border="left" class="mt-2" color="deep-orange" icon="mdi-alert-circle-outline"
          v-if="objectsWithoutGroup.length > 0"
        >
          {{ transc('settlement.objectsDoNotBelongToGroup', objectsWithoutGroup.length, {numberOfObjectsWithoutGroup: objectsWithoutGroup.length}) }}:
          <ul>
            <li
              v-for="(objectName, i) in objectsWithoutGroup"
              :key="i"
            >{{ objectName }}</li>
          </ul>
        </v-alert>
        <v-alert
          v-if="otherBookingFatalErrors > 0"
          text border="left" class="mt-2" color="deep-orange" icon="mdi-alert-circle-outline">
          {{ transc('settlement.bookingsWithFatalErrors', otherBookingFatalErrors.length, {numberOfBookings: otherBookingFatalErrors.length}) }}
        </v-alert>
        <v-alert dense text border="left" class="mt-1" icon="mdi-check-circle-outline">
          {{ transc('settlement.bookingsWillBeAutoShifted', bookingValidationResult.autoShiftable.length, {numberOfBookings: bookingValidationResult.autoShiftable.length}) }}
        </v-alert>
        <v-alert dense text border="left" class="mt-1" icon="mdi-check-circle-outline">
          {{ transc('settlement.bookingsWillBeAutoClosed', bookingValidationResult.autoClosable.length, {numberOfBookings: bookingValidationResult.autoClosable.length}) }}
        </v-alert>

        <v-alert
          v-if="bookingValidationResult.manual.length > 0"
          text border="left" class="mt-1" color="primary" icon="mdi-alert-circle-outline">
          {{ transc('settlement.bookingsRequireManualAction', bookingValidationResult.manual.length, {numberOfBookings: bookingValidationResult.manual.length}) }}:
        </v-alert>
        <v-alert
          v-else
          text border="left" class="mt-1" color="success" icon="mdi-check-circle">
          {{ transc('settlement.noFurtherActionsRequired') }}
        </v-alert>

        <div class="d-flex justify-end">
          <v-btn
            color="primary"
            large
            class="mb-2"
            :loading="isSettlementGenerating"
            @click="updateBookings"
          >
            <v-icon left>mdi-book-plus</v-icon>{{ trans('settlement.updateBookings') }}</v-btn>
        </div>

        <v-data-table
          v-if="bookingValidationResult.manual.length > 0"
          :items="bookingValidationResult.manual"
          :items-per-page="100"
          :footer-props="{itemsPerPageOptions: [50, 100], disableItemsPerPage: true}"
          :hide-default-footer="bookingValidationResult.manual.length <= 100"
          :headers="[
            {text: trans('booking.bookingId'), value: 'id'},
            {text: transc('object.object'), value: 'objectName'},
            {text: trans('guest.guest'), value: 'guestName'},
            {text: trans('booking.checkinAt'), value: 'checkinAt'},
            {text: trans('booking.checkoutAt'), value: 'checkoutAt'},
            {text: trans('booking.validationErrorCodes'), value: 'errorCodes'},
            {text: trans('general.action'), value: 'action', width: '1px'},
          ]"
        >
          <template v-slot:item.id="{item}">
            <booking-id :value="item.id"/>
          </template>

          <template v-slot:item.objectName="{ item }">
            <object-name :value="item" />
          </template>

          <template v-slot:item.guestName="{ item }">
            <guest-name :value="item"/>
          </template>

          <template v-slot:item.checkinAt="{item}">
            <span class="unwrappable">{{ formatDate(item.checkinAt, 'L') }}</span>
          </template>

          <template v-slot:item.checkoutAt="{item}">
            <span class="unwrappable">{{ formatDate(item.checkoutAt, 'L') }}</span>
          </template>

          <template v-slot:item.errorCodes="{item}">
            <template v-for="errorCode in item.errorCodes">
              <booking-validation-error
                :key="errorCode"
                :value="errorCode"
                :booking="item"
                only-icon
              />
            </template>
          </template>

          <template v-slot:item.action="{item}">
            <div class="unwrappable">
              <v-btn
                :color="isBookingManualActionSelected(item.id, undefined) ? 'primary' : null"
                @click="setBookingManualAction(item.id, undefined)"
                :title="trans('settlement.optionNothingTitle')"
                class="elevation-0"
              >
                <v-icon left style="opacity: 0.5">{{ isBookingManualActionSelected(item.id, undefined) ? 'mdi-checkbox-marked-circle-outline' : 'mdi-checkbox-blank-circle-outline' }}</v-icon>
                <span>{{ trans('settlement.optionNothing') }}</span>
              </v-btn>
              <v-btn
                :color="isBookingManualActionSelected(item.id, bookingManualActionsOptions.SHIFT) ? 'primary' : null"
                @click="setBookingManualAction(item.id, bookingManualActionsOptions.SHIFT)"
                :title="trans('settlement.optionShiftTitle')"
                class="elevation-0 ml-1"
              >
                <v-icon left style="opacity: 0.5">{{ isBookingManualActionSelected(item.id, bookingManualActionsOptions.SHIFT) ? 'mdi-checkbox-marked-circle-outline' : 'mdi-checkbox-blank-circle-outline' }}</v-icon>
                <span>{{ trans('booking.shift') }}</span>
              </v-btn>
            </div>
          </template>
        </v-data-table>

        <div class="d-flex justify-end">
          <v-btn
            v-if="bookingValidationResult.manual.length > 10"
            color="primary"
            large
            class="mt-6"
            :loading="isSettlementGenerating"
            @click="updateBookings"
          >
            <v-icon left>mdi-book-plus</v-icon>{{ trans('settlement.updateBookings') }}</v-btn>
        </div>
      </template>
    </request-state>

    <validation-errors-modal
      :title="trans('settlement.settlementObjects.titleRenewError')"
      :show-modal.sync="showBookingUpdateErrorModal"
      :validation-errors="bookingUpdateResult ? bookingUpdateResult.objectExceptions : {}"
      @close="goToSettlement"
    />

  </v-container>
</template>

<script>
import {LocaleUtils} from '../utils/locale-utils';
import PageHeading from '../components/PageHeading';
import {DateUtils} from '../utils/date-utils';
import BackButton from '../components/BackButton';
import RequestState from '../components/RequestState';
import {MainUtils} from '../utils/main-utils';
import {DevUtils} from '../utils/dev-utils';
import {RouterUtils} from '../utils/router-utils';
import {BookingValidationErrors} from '@/lib/booking-validation-errors';
import BookingValidationError from '@/components/BookingValidationError';
import {SettlementApi} from '@/api/settlement-api';
import BookingId from "@/components/BookingId";
import ObjectName from '@/components/ObjectName';
import GuestName from "@/components/GuestName";
import ValidationErrorsModal from '@/components/ValidationErrorsModal';

const bookingManualActionsOptions = {
  SHIFT: 'SHIFT',
  CLOSE: 'CLOSE'
};

export default {
  name: 'SettlementUpdateBookingsPage',
  mixins: [
    LocaleUtils,
    DateUtils,
    MainUtils,
    DevUtils,
    RouterUtils
  ],
  components: {
    BookingValidationError,
    ValidationErrorsModal,
    RequestState,
    BackButton,
    PageHeading,
    BookingId,
    ObjectName,
    GuestName
  },
  props: {
    settlementId: {
      required: true
    },
  },
  data: () => ({
    /** @type {BookingValidationResult} */
    bookingValidationResult: null,
    /** @type {SettlementRenewResultDto} */
    bookingUpdateResult: null,
    showBookingUpdateErrorModal: false,
    validationPromise: null,
    bookingManualActionsOptions: bookingManualActionsOptions,
    bookingManualActions: {},
    settlementGeneratePromise: null,
    isSettlementGenerating: false,
    /** @type {SettlementDto} */
    settlement: null,
  }),

  computed: {
    objectsWithoutGroup () {
      return this.bookingValidationResult && this.bookingValidationResult.error.length > 0
        ?  this.bookingValidationResult.error.filter(errorBooking => errorBooking.errorCodes.includes(BookingValidationErrors.BOOKING_NO_GROUP.value))
            .map(errorBooking => errorBooking.objectName)
            .filter(this.onlyUnique)
        : [];
    },
    otherBookingFatalErrors () {
      return this.bookingValidationResult && this.bookingValidationResult.error.length > 0
        ?  this.bookingValidationResult.error.filter(errorBooking => !errorBooking.errorCodes.includes(BookingValidationErrors.BOOKING_NO_GROUP.value))
        : [];
    },

    bookingIdsToShift () {
      let bookingIdsToShift = [];
      for (let bookingId in this.bookingManualActions) {
        if (this.bookingManualActions[bookingId] === bookingManualActionsOptions.SHIFT) {
          bookingIdsToShift.push(parseInt(bookingId));
        }
      }
      return bookingIdsToShift;
    },

    pageTitle () {
      if (this.settlement) {
        return this.getMonthNameByIndex(this.settlement.month) + ' ' +  this.settlement.year + ' - ' + this.transSingular('settlement.settlement', 1);
      }
      return null;
    }
  },

  mounted () {
    SettlementApi.getById(this.settlementId)
    .then(response => {
      this.settlement = response.body;
    })
    .catch(this.handleErrorResponse);

    if (this.bookingValidationResult === null) {
      this.validateBookings();
    }
  },

  methods: {
    validateBookings () {
      this.validationPromise = SettlementApi.validateSettlementBookings(this.settlementId);

      this.validationPromise.then(response => {
        this.bookingValidationResult = response.data;
      });
    },
    isBookingManualActionSelected (bookingId, option) {
      return this.bookingManualActions[bookingId] === option;
    },
    setBookingManualAction (bookingId, option) {
      this.$set(this.bookingManualActions, bookingId, option);
    },
    updateBookings () {
      this.settlementGeneratePromise = SettlementApi.updateSettlementBookings(
          this.settlementId,
          {
            bookingIdsToShift: this.bookingIdsToShift
          }
      );

      this.isSettlementGenerating = true;

      this.settlementGeneratePromise.then(response => {
        this.showSavedSuccessfullyNotification();
        this.bookingUpdateResult = response.data;
        if(Object.keys(this.bookingUpdateResult.objectExceptions).length > 0) {
          this.showBookingUpdateErrorModal = true;
        } else {
          this.goToSettlement();
        }
      });

      this.settlementGeneratePromise.catch(response => {
        if (response.data.message) {
          this.showErrorNotification(response.data.message);
        } else {
          this.showErrorNotification();
        }
      });

      this.settlementGeneratePromise.finally(response => {
        this.isSettlementGenerating = false;
      });
    },
    goToSettlement() {
      if(this.bookingUpdateResult && this.bookingUpdateResult.settlement.id) {
        this.pushToRoute('SettlementDetails', {settlementId: this.bookingUpdateResult.settlement.id});
      }
    }
  }
}
</script>

